import { DropDownList } from '@progress/kendo-react-dropdowns';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ColumnEditorDropDown from 'src/components/ColumnEditorDropDown';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import { useContextSelector } from 'use-context-selector';
import { ViewCatalogsContext } from './ViewCatalogsContext';
import { DEFAULT_SELECTED_CATEGORY, getCategoryDropdownOptions } from './ViewCatalogsUtils';

interface SortOption {
  value: string;
  label: string;
}

const baseSortOptions: SortOption[] = [
  { value: 'catalogName-asc', label: 'Name - Ascending' },
  { value: 'catalogName-desc', label: 'Name - Descending' },
];

const ViewCatalogsGridHeader: FC = () => {
  const { t } = useTranslation();

  const categoryOptions = useContextSelector(ViewCatalogsContext, (s) => s.categoryOptions);
  const selectedCategory = useContextSelector(ViewCatalogsContext, (s) => s.selectedCategory);
  const setSelectedCategory = useContextSelector(ViewCatalogsContext, (s) => s.setSelectedCategory);
  const selectedSupplier = useContextSelector(ViewCatalogsContext, (s) => s.selectedSupplier);
  const setSelectedSupplier = useContextSelector(ViewCatalogsContext, (s) => s.setSelectedSupplier);
  const dataState = useContextSelector(ViewCatalogsContext, (s) => s.dataState);
  const setDataState = useContextSelector(ViewCatalogsContext, (s) => s.setDataState);
  const hasActiveFilters = useContextSelector(ViewCatalogsContext, (s) => s.hasActiveFilters);
  const clearFilters = useContextSelector(ViewCatalogsContext, (s) => s.clearFilters);

  const sortOptions = useMemo(
    () => baseSortOptions.map((option) => ({ ...option, label: t(option.label) })),
    [t]
  );

  const selectedSortOption = useMemo(() => {
    const sort = dataState.sort?.[0];
    if (!sort?.field || !sort.dir) {
      return sortOptions[0];
    }

    return (
      sortOptions.find((option) => option.value === `${sort.field}-${sort.dir}`) ?? sortOptions[0]
    );
  }, [dataState.sort, sortOptions]);

  const supplierDropdownData = useMemo(
    () => selectedSupplier.map((item) => ({ ...item, title: t(item.title) })),
    [selectedSupplier, t]
  );

  const categoryDropdownOptions = useMemo(
    () => getCategoryDropdownOptions(categoryOptions, t('All Categories')),
    [categoryOptions, t]
  );

  return (
    <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2 bg-[#233843] px-2 py-1">
      {/* Clear Filters Button */}
      <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters} />
      <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
        {/* Supplier Dropdown */}
        <div className="min-w-0" style={{ width: 200 }}>
          <ColumnEditorDropDown
            data={supplierDropdownData}
            onChange={(e) => setSelectedSupplier(e)}
            label={t('All Suppliers')}
            value={t('All Suppliers')}
          />
        </div>
        <div className="min-w-0" style={{ width: 200 }}>
          {/* Category Dropdown */}
          <DropDownList
            id="view-drop-down-list"
            data={categoryDropdownOptions}
            onChange={(e) =>
              setSelectedCategory(
                e.value.categoryId ? e.value : DEFAULT_SELECTED_CATEGORY
              )
            }
            style={{ width: 200 }}
            value={selectedCategory}
            textField="description"
            dataItemKey="categoryId"
            popupSettings={{ className: 'toolbar-dropdown-popup' }}
          />
        </div>
        {/* Sort by Dropdown */}
        <div className="flex min-w-0 flex-shrink-0 items-center gap-2">
          <label htmlFor="browse-catalogs-sort-by" className="whitespace-nowrap text-sm text-white">
            {t('Sort by')}
          </label>
          <DropDownList
            id="view-drop-down-list"
            style={{ width: 200 }}
            data={sortOptions}
            textField="label"
            dataItemKey="value"
            value={selectedSortOption}
            onChange={(e) => {
              const [field, dir] = e.value.value.split('-');
              setDataState({
                ...dataState,
                sort: [{ field, dir: dir as 'asc' | 'desc' }],
              });
            }}
            popupSettings={{ className: 'toolbar-dropdown-popup' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewCatalogsGridHeader;
